import { useQuery } from '@tanstack/react-query';
import { api, unwrap } from '../../api/client';
import type { components } from '../../api/schema';
import { Badge, type Tone } from '../../design';
import { STATUS as LOCKER_STATUS } from '../lockers/common';

export type Operation = components['schemas']['Operation'];
export type OperationDetail = components['schemas']['OperationDetail'];
export type OperationStatus = components['schemas']['OperationStatus'];
export type AccessGrant = components['schemas']['AccessGrant'];
export type GrantStatus = components['schemas']['GrantStatus'];
export type GrantPurpose = components['schemas']['GrantPurpose'];
/** `Recipient` are `anyOf` (e-mail sau telefon) și iese `unknown` din generator; forma reală: */
export type Recipient = { name: string; email?: string | null; phone?: string | null };
export function recipientOf(o: { recipient: unknown }): Recipient {
  return (o.recipient ?? { name: '' }) as Recipient;
}

export const OPERATION_STATUS: Record<OperationStatus, { label: string; tone: Tone }> = {
  pending: { label: 'Așteaptă încărcare', tone: 'orange' },
  ready: { label: 'Pregătită de ridicare', tone: 'blue' },
  inprogress: { label: 'În desfășurare', tone: 'purple' },
  done: { label: 'Finalizată', tone: 'green' },
  cancelled: { label: 'Anulată', tone: 'neutral' },
  expired: { label: 'Termen depășit', tone: 'red' },
};

export const GRANT_STATUS: Record<GrantStatus, { label: string; tone: Tone }> = {
  active: { label: 'Activă', tone: 'green' },
  used: { label: 'Utilizată', tone: 'neutral' },
  expired: { label: 'Expirată', tone: 'orange' },
  revoked: { label: 'Revocată', tone: 'red' },
};

export const GRANT_PURPOSE: Record<GrantPurpose, string> = {
  deposit: 'Depunere',
  pickup: 'Ridicare',
};

export function OperationStatusBadge({ status }: { status: OperationStatus }) {
  return <Badge tone={OPERATION_STATUS[status].tone}>{OPERATION_STATUS[status].label}</Badge>;
}

export function GrantStatusBadge({ status }: { status: GrantStatus }) {
  return <Badge tone={GRANT_STATUS[status].tone}>{GRANT_STATUS[status].label}</Badge>;
}

/** Lockerele din perimetru pentru select-uri (nume + stare); maximum 100. */
export function useLockerOptions(orgId?: string) {
  return useQuery({
    queryKey: ['lockers', 'options', orgId ?? ''],
    queryFn: () =>
      unwrap(
        api.GET('/lockers', { params: { query: { pageSize: 100, orgId: orgId || undefined } } }),
      ),
    staleTime: 30_000,
  });
}

export function lockerOptionLabel(l: components['schemas']['Locker']): string {
  return `${l.name} · ${LOCKER_STATUS[l.status].label}`;
}

/** `total`-ul unei liste filtrate, pentru KPI (D10: agregare în UI, fără rute noi). */
export function useCount(
  key: unknown[],
  fetcher: () => Promise<{ total: number }>,
): { total: number | undefined; isLoading: boolean } {
  const q = useQuery({ queryKey: ['count', ...key], queryFn: fetcher, staleTime: 15_000 });
  return { total: q.data?.total, isLoading: q.isLoading };
}
